<script setup lang="ts">
import { ref, watch, onUnmounted } from 'vue'
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxRoot,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxViewport
} from 'reka-ui'
import ProgressSpinner from 'primevue/progressspinner'
import { queryUsersInDomain } from './api'
import { ContributorUserInfo } from './library_types'

const props = defineProps({
  domainUuid: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    default: 'Name or email address'
  },
  selectedUsersExternal: {
    type: Array as () => ContributorUserInfo[],
    default: () => []
  }
})

const emit = defineEmits(['user-selected', 'user-removed'])

const inputValue = ref<string>('')
const isLoading = ref<boolean>(false)
const options = ref<Array<{name: string, children: Array<{name: string, value: string, userData: ContributorUserInfo}>}>>([])
const selectedUsers = ref<ContributorUserInfo[]>([])
const loadError = ref<boolean>(false)
const searchCache = ref<{[key: string]: ContributorUserInfo[]}>({})
const MIN_SEARCH_CHARS = 2
const SEARCH_DEBOUNCE_MS = 300

let debounceTimeout: number | null = null

watch(() => props.selectedUsersExternal, (newValue) => {
  selectedUsers.value = newValue
}, { immediate: true })

function debouncedSearch(query: string, delay: number = SEARCH_DEBOUNCE_MS): void {
  if (debounceTimeout !== null) {
    clearTimeout(debounceTimeout)
  }

  debounceTimeout = window.setTimeout(async () => {
    if (query.length < MIN_SEARCH_CHARS) return

    isLoading.value = true
    loadError.value = false

    try {
      if (searchCache.value[query]) {
        options.value = [
          {
            name: 'Users',
            children: searchCache.value[query].map((user: ContributorUserInfo) => ({
              name: `${user.Name} <br/> ${user.Email}`,
              value: user.UserUUID,
              userData: user
            }))
          }
        ]
        return
      }
      const users = await queryUsersInDomain(props.domainUuid, query)

      options.value = [
        {
          name: 'Users',
          children: users.map((user: ContributorUserInfo) => ({
            name: `${user.Name} ${user.Email}`,
            value: user.UserUUID,
            userData: user
          }))
        }
      ]
      searchCache.value[query] = users
    } catch (error) {
      console.error('Error fetching users:', error)
      options.value = []
      loadError.value = true
    } finally {
      isLoading.value = false
    }
  }, delay)
}

onUnmounted(() => {
  if (debounceTimeout !== null) {
    clearTimeout(debounceTimeout)
  }
})

watch(inputValue, (newValue: string) => {
  if (newValue.length < MIN_SEARCH_CHARS) {
    options.value = []
    return
  }

  debouncedSearch(newValue)
})

function handleSelect(value: string | string[]): void {
  if (!value) {
    return
  }

  let valueToProcess: string;
  if (Array.isArray(value)) {
    valueToProcess = value[value.length - 1];
  } else {
    valueToProcess = value as string;
  }

  if (valueToProcess) {
    for (const group of options.value) {
      for (const option of group.children) {
        if (option.value === valueToProcess) {
          if (!selectedUsers.value.some(user => user.UserUUID === option.userData.UserUUID)) {
            selectedUsers.value.push(option.userData)
            emit('user-selected', option.userData)
          }
          inputValue.value = ''
          return
        }
      }
    }
  }
}

function removeSelectedUser(userUuid: string): void {
  selectedUsers.value = selectedUsers.value.filter(user => user.UserUUID !== userUuid)
  emit('user-removed', userUuid)
}
</script>

<template>
  <div class="relative w-full">
    <ComboboxRoot
      class="relative w-full"
      :modelValue="selectedUsers.map(user => user.UserUUID)"
      :multiple="true"
      @update:modelValue="handleSelect"
    >
    <ComboboxAnchor class="w-full flex items-center flex-wrap rounded-sm border-gray-700 border px-4 py-3 text-lg leading-none bg-white text-gray-800 data-[placeholder]:text-gray-700 outline-none">
      <div v-if="selectedUsers.length > 0" class="flex flex-row gap-2 items-center mr-2">
        <div
            v-for="user in selectedUsers"
            :key="user.UserUUID"
            class="inline-flex items-center bg-white border border-gray-700 text-gray-800 rounded-full px-3 py-1 mr-2 mb-1"
        >
            <span class="mr-1 text-sm fs-exclude">{{ user.Name }}</span>
            <button
            @click="removeSelectedUser(user.UserUUID)"
            class="text-primary-600 hover:text-primary-800 focus:outline-none remove-user"
            aria-label="Remove user"
            >
            <span class="material-icons-outlined text-sm">close</span>
            </button>
        </div>
      </div>
      <ComboboxInput
        v-model="inputValue"
        class="!bg-transparent outline-none text-gray-800 shrink selection:bg-primary-100 placeholder-gray-700 grow fs-exclude"
        :placeholder="selectedUsers.length > 0 ? '' : placeholder"
        aria-label="User search"
      />
      <ComboboxTrigger>
        <ProgressSpinner v-if="isLoading" class="h-4 w-4 text-gray-500" />
      </ComboboxTrigger>
    </ComboboxAnchor>

    <ComboboxContent class="absolute z-10 w-full mt-1 bg-white overflow-hidden rounded-md shadow-md border will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade" v-if="options.length > 0 || isLoading || loadError" aria-label="User search results">
      <ComboboxViewport class="p-1">
        <ComboboxEmpty class="text-gray-900 text-md font-medium p-2">
            <span v-if="loadError">We couldn't load users. Please try again later.</span>
            <span v-else-if="isLoading">Loading...</span>
            <span v-else>No users found.</span>
        </ComboboxEmpty>

        <template
          v-for="(group, index) in options"
          :key="group.name"
        >
          <ComboboxGroup>
            <ComboboxSeparator
              v-if="index !== 0"
              class="h-[1px] bg-gray-200 m-1"
            />

            <ComboboxItem
              v-for="option in group.children"
              :key="option.value"
              :value="option.value"
              class="selectable-user leading-none text-gray-800 rounded flex items-center py-2 pr-8 pl-4 relative select-none data-[disabled]:text-gray-400 data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[highlighted]:bg-primary-100 data-[highlighted]:text-primary-900"
            >
              <ComboboxItemIndicator
                class="absolute left-0 w-6 inline-flex items-center justify-center"
              ></ComboboxItemIndicator>
              <div class="flex flex-col gap-1">
                <span class="text-md font-medium fs-exclude">
                  {{option.userData.Name}}
                </span>
                <span class="text-sm fs-exclude">
                  {{option.userData.Email}}
                </span>
              </div>
            </ComboboxItem>
          </ComboboxGroup>
        </template>
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxRoot>
  </div>
</template>